import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ReactNode } from 'react';
import { type VariantProps, tv } from 'tailwind-variants';

import ChevronRightIcon from '@cartwhl/ui/icons/ChevronRightIcon';

import Text from '../base/Text';
import './Dropdown.css';

export default function Dropdown({
  trigger,
  children,
  onOpenChange,
  side,
  align = 'end',
  disabled,
  width,
  inModal = false,
}: {
  trigger: ReactNode;
  children: ReactNode;
  onOpenChange?: (open: boolean) => void;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  disabled?: boolean;
  width?: number;
  inModal?: boolean;
}) {
  return (
    <DropdownMenu.Root onOpenChange={onOpenChange} modal={false}>
      <DropdownMenu.Trigger asChild disabled={disabled}>
        {trigger}
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side={side}
          className="RxDropdownContent text-gray-90 dark:bg-gray-75 shadow-menu min-w-fit rounded-2xl bg-white p-2 dark:text-white"
          align={align}
          collisionPadding={8}
          sideOffset={8}
          style={{ width, zIndex: inModal ? 10000 : 20 }}
        >
          <Text as="div" variant="studio/body/medium/sm">
            {children}
          </Text>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

const itemInner = tv({
  base: 'group flex w-full items-center rounded-lg py-2.5 pl-3 pr-3 transition-colors dark:text-white',
  variants: {
    variant: {
      default: '',
      blue: 'bg-blue/10',
    },
    disabled: {
      true: 'cursor-default opacity-50',
      false: 'cursor-pointer',
    },
    justify: {
      start: 'justify-start',
      center: 'justify-center',
      end: 'justify-end',
    },
  },
  compoundVariants: [
    {
      variant: 'default',
      disabled: false,
      className: 'hover:bg-gray-15 dark:hover:bg-gray-90',
    },
    {
      variant: 'blue',
      disabled: false,
      className:
        'hover:bg-blue dark:hover:bg-blue hover:text-white dark:hover:text-white',
    },
  ],
  defaultVariants: {
    variant: 'default',
    disabled: false,
  },
});
Dropdown.Item = function Item({
  children,
  href,
  onClick,
  icon,
  disabled = false,
  variant,
  className,
  justify,
}: {
  children: ReactNode;
  href?: string;
  onClick?: () => void;
  icon?: ReactNode;
  disabled?: boolean;
  variant?: VariantProps<typeof itemInner>['variant'];
  justify?: VariantProps<typeof itemInner>['justify'];
  className?: string;
}) {
  const Element = href ? 'a' : 'button';
  const path = href?.split('?')[0];
  const isExternal = path && !path.includes('.getcartwheel.com');

  return (
    <DropdownMenu.Item
      className={`outline-hidden overflow-hidden rounded-lg ${className}`}
      onClick={(e) => e.stopPropagation()}
      disabled={disabled}
    >
      <Element
        href={href}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noreferrer noopener' : undefined}
        onClick={onClick}
        className={itemInner({ variant, disabled, justify })}
      >
        {icon !== undefined && <span className="mr-3 w-5">{icon}</span>}
        <Text variant="studio/body/medium/sm">{children}</Text>
      </Element>
    </DropdownMenu.Item>
  );
};

Dropdown.Sub = function Sub({
  children,
  text,
  icon,
}: {
  children: ReactNode;
  text: string;
  icon?: ReactNode | null | undefined; // Null means: empty icon. Undefined means no icon.
}) {
  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger className="dark:hover:bg-gray-90 hover:bg-gray-15 data-[state=open]:bg-gray-15 dark:data-[state=open]:bg-gray-90 outline-hidden flex w-[224px] cursor-default rounded-lg py-2.5 pl-3 pr-3 transition-colors">
        {icon !== undefined && <span className="mr-3 w-5">{icon}</span>}
        <Text variant="studio/body/medium/sm">{text}</Text>
        <ChevronRightIcon className="text-gray-45 ml-auto" />
      </DropdownMenu.SubTrigger>
      <DropdownMenu.Portal>
        <DropdownMenu.SubContent className="RxSubContent text-gray-90 dark:bg-gray-75 shadow-menu z-20 mr-[-150px] rounded-2xl bg-white p-2 sm:mr-0 dark:text-white">
          <Text variant="studio/body/medium/sm">{children}</Text>
        </DropdownMenu.SubContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Sub>
  );
};

Dropdown.Separator = function Separator() {
  return (
    <DropdownMenu.Separator className="bg-gray-15 dark:bg-gray-60 -mx-2 my-2 h-px" />
  );
};
